import { Component, h, Prop, State, Watch } from "@stencil/core"
import { PaymentMethodEnum } from "../../enums/PaymentMethodEnum"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { OtpChannelModel } from "./OtpChannelModel"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"
import { ItemRadioModel } from "../itemsRadioList/ItemsRadioListModel"
import { srcScreenError } from "../../helpers/CommonRenders"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { getSupportedLocaleData, getLanguageHandler, getLocale } from "../../languageHandler/LanguageHandler"

/**
 * Entire screen for choosing the way of delivering the OTP to the customer.
 */
@Component({
  tag: "otp-channel",
  styleUrl: "OtpChannel.scss",
})
export class OtpChannelScreen {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /**
   * Indicator which Click to Pay System is supported.
   */
  @Prop() paymentMethod: PaymentMethodEnum

  /**
   * Data source for alert which can be displayed on the OtpChannel.
   */
  @Prop() infoMessage?: InfoMessageModel | undefined

  /**
   * Informations about values of possible OTP channels.
   */
  @Prop() otpData: OtpChannelModel

  /**
   * Callback called after close button click.
   */
  @Prop() closeButtonOnClick?: () => void

  /**
   * Callback called after continue button click.
   *
   * @param selectedItem Object with data about chosen OTP channel.
   */
  @Prop() continueButtonOnClick?: (selectedItem: ItemRadioModel) => void

  @State() selectedItem: ItemRadioModel
  @State() itemList: ItemRadioModel[] = []

  @Watch("otpData")
  handleDataChange(newValue: OtpChannelModel) {
    if (!newValue || !Object.entries(newValue).filter(([_, otpChannelValue]) => !!otpChannelValue).length) return
    this.itemList = this.generateItemList(newValue)
  }

  generateItemList = (value: OtpChannelModel) => {
    const changeOTPChannelOption3String = this.languageHandler.getString(`changeOTPChannel.option3`)
    return Object.keys(value).reduce(
      (prev: ItemRadioModel[], key: string, index: number): ItemRadioModel[] => {
        const labelString = this.languageHandler.getString(`changeOTPChannel.option${index + 1}`)

        const entry: string = this.otpData[key]
        const obj: ItemRadioModel = {
          label: labelString ? labelString : "",
        }
        if (entry) {
          obj["description"] = entry
        }

        return [obj, ...prev]
      },
      [{ label: changeOTPChannelOption3String ? changeOTPChannelOption3String : "" }]
    )
  }

  private horizontalMarkSize: HorizontalMarkSizeEnum
  private languageHandler: LanguageHandler
  private errorMessage: string[]
  private supportedLocale: string

  componentWillLoad() {
    this.errorMessage = []

    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    }

    if (this.errorMessage.length > 0) {
      return
    }

    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.supportedLocale = getLocale(localeData)

    this.itemList = this.generateItemList(this.otpData)

    if (this.infoMessage && this.infoMessage.infoMessage.length > MessageMaxLength.CHANGE_OTP_CHANNEL) {
      this.infoMessage.infoMessage = this.infoMessage.infoMessage.substring(0, MessageMaxLength.CHANGE_OTP_CHANNEL)
    }
  }

  setSelectedItem = (item: ItemRadioModel) => {
    this.selectedItem = item
  }

  getSelectedItem = (item: ItemRadioModel) => this.selectedItem.label === item.label

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("OTP Channel", this.errorMessage)
    }

    return (
      <div class="otpChannel">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick}></close-button>
          )}
        </div>
        <div class="channelTopParent">
          <div class="channelTopTitle">{this.languageHandler.getString("clickToPay")}</div>
          <horizontal-mark
            class="channelSrcMark"
            locale={this.supportedLocale}
            paymentMethod={this.paymentMethod}
            horizontalMarkSize={this.horizontalMarkSize}></horizontal-mark>
        </div>
        {this.infoMessage && Boolean(this.infoMessage.infoMessage) && (
          <div class={"channelInfoText"}>
            <info-message infoMessage={this.infoMessage} />
          </div>
        )}
        <div class="channelTopSubTitle">{this.languageHandler.getString("changeOTPChannel.subtitle")}</div>
        <div class="otpChannelItemsRadioList">
          <item-radio-list itemList={this.itemList} setSelectedItem={this.setSelectedItem}></item-radio-list>
        </div>
        <div class="channelButtonDiv">
          {typeof this.continueButtonOnClick === "function" && (
            <button
              class="otpChannelContinueButton"
              onClick={() => this.continueButtonOnClick && this.continueButtonOnClick(this.selectedItem)}>
              {this.languageHandler.getString("continue")}
            </button>
          )}
        </div>
      </div>
    )
  }
}
